import type React from "react";
import { useContext, useEffect, useState } from "react";

import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { faChartMixed, faGraduationCap, faPersonWalking } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Stack, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled, { useTheme } from "styled-components";
import invariant from "ts-invariant";

import { usePostMessage } from "api/services/messaging/hooks";
import type { ActivityType } from "api/services/treatment/schemas/ScheduleWeek";
import { useProfileContext } from "contexts/ProfileContext";
import { useSavedInputsContext } from "contexts/SavedInputsContext";
import { MessageInput } from "routes/messages/components/Conversation/MessageInput";
import type { PreviewActivity } from "routes/patients/views/Treatment/components/TreatmentEditor/Modals";
import { ActivityPreviewModal } from "routes/patients/views/Treatment/components/TreatmentEditor/Modals";
import { BaseButton } from "shared/atoms/BaseButton";
import { Notification } from "shared/atoms/Notification";
import { Tag } from "shared/atoms/Tag";
import { WidgetBox } from "shared/containers/WidgetBox/WidgetBox";
import { LeifGPT } from "shared/LeifGPT";
import { SmartReplyIndicator } from "shared/LeifGPT/LeifGPT";
import { MarkAsUnread } from "shared/molecules/MarkAsUnread";
import { AnalyticsEvents, AnalyticsService } from "utils/analytics";
import { CurrentPatientContext } from "utils/contexts";
import { useLocalizedDate } from "utils/date";
import { reportError } from "utils/errorReporting";

export interface Props {
  completedOn?: string;
  reply?: string;
  activity: ActivityType;
  activityId?: number | null;
  activityName: string;
  levels: number;
  currentLevel: number;
  comment: string;
  id: number;
  viewed: boolean;
  smartReply: string | null | undefined;
  smartReplyId: number | null | undefined;
  markCommentAsUnviewed: () => void;
}

type ActivityConfig = {
  icon: IconDefinition;
  color: "purple" | "orange" | "green";
};

const getActivityConfig = (type: ActivityType): ActivityConfig => {
  switch (type) {
    case "exercise":
      return { icon: faPersonWalking, color: "orange" };
    case "lesson":
      return { icon: faGraduationCap, color: "green" };
    default:
      return { icon: faChartMixed, color: "purple" };
  }
};

export const Comment: React.FC<Props> = ({
  completedOn,
  reply,
  activity,
  activityId,
  activityName,
  levels,
  currentLevel,
  comment,
  id,
  viewed = true,
  smartReply,
  smartReplyId,
  markCommentAsUnviewed,
}) => {
  const { t } = useTranslation();
  const { format, parseISO } = useLocalizedDate();
  const [showForm, setShowForm] = useState(false);
  const [previewActivity, setPreviewActivity] = useState<PreviewActivity | null>(null);
  const { patientId } = useParams<{ patientId: string }>();
  const [error, setError] = useState<boolean | null>(null);
  const [localReply, setLocalReply] = useState(reply);
  const { profile } = useProfileContext();
  const { updateIsCreatable } = useContext(CurrentPatientContext);
  const { savedComments, setSavedComments } = useSavedInputsContext();
  const theme = useTheme();
  const postMessage = usePostMessage();

  invariant(profile);

  const form = useForm<{ body: string }>({
    defaultValues: { body: "" },
  });

  useEffect(() => {
    form.setValue("body", savedComments[id] ?? "");
  }, []);

  useEffect(() => {
    return () => {
      const currentBody = form.getValues("body");
      setSavedComments(prev => {
        const next = { ...prev };
        if (currentBody) {
          next[id] = currentBody;
        } else {
          delete next[id];
        }
        return next;
      });
    };
  }, []);

  const clearSavedComment = () => {
    setSavedComments(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  };

  const handleSubmitComment = async (body: string) => {
    try {
      await postMessage.mutateAsync(
        { activity_id: id, body, to: Number(patientId) },
        {
          onSuccess: data => {
            AnalyticsService.track(AnalyticsEvents.PATIENTS.COMMENT_REPLY, {
              comment_id: id,
              patient: patientId,
            });

            setError(null);
            setShowForm(false);
            if (data.message.body) {
              setLocalReply(data.message.body);
            }
            updateIsCreatable();
            clearSavedComment();
            form.reset();
          },
        }
      );
    } catch (e) {
      setError(true);
      reportError("Comment onSubmit error", new Error(JSON.stringify(e)));
    }
  };

  const onSubmit = form.handleSubmit(({ body }) => {
    handleSubmitComment(body);
  });

  const { icon, color } = getActivityConfig(activity);

  const canPreview = !!activityId && activity === "exercise";

  const titleContent = (
    <Stack gap={1} direction="row" alignItems="center">
      <Typography variant="body_bold">{activityName}</Typography>
      {activity === "exercise" && currentLevel !== 0 && (
        <LevelBadge>
          {currentLevel}/{levels}
        </LevelBadge>
      )}
      {canPreview && (
        <BaseButton
          variant="action"
          icon="eye"
          size="small"
          title={t("treatment.editor.preview_activity", { activityType: activity })}
          onClick={() => {
            if (activityId == null) return;
            setPreviewActivity({
              id: activityId,
              type: "exercise",
              part: currentLevel || 1,
            });
          }}
        />
      )}
    </Stack>
  );

  return (
    <div data-testid="comment">
      <WidgetBox
        icon={<FontAwesomeIcon icon={icon} />}
        color={color}
        smallPadding
        bordered
        title={titleContent}
        toolbar={
          <Stack gap={1} direction="row" alignItems="center">
            <Typography variant="subtitle1" sx={{ color: theme.colors.redesign.db70, lineHeight: "28px" }}>
              {completedOn && format(parseISO(completedOn), "PP")}
            </Typography>
            {viewed ? (
              <MarkAsUnread commentCallBack={markCommentAsUnviewed} />
            ) : (
              <Tag dataTestId="new-comment-tag" noMargin label={t("common.new")[0]} />
            )}
          </Stack>
        }
      >
        <CommentRow>
          <Typography variant="subtitle1">{comment}</Typography>
          {!localReply && (
            <Stack direction="row" alignItems="center" gap={1}>
              {!showForm && (
                <>
                  <BaseButton
                    dataTestID="comment-reply-button"
                    onClick={() => setShowForm(true)}
                    variant="action"
                    icon="pen-to-square"
                    text={t("buttons.reply")}
                  />
                  {smartReply && <SmartReplyIndicator />}
                </>
              )}
            </Stack>
          )}
        </CommentRow>

        {localReply && (
          <Reply data-testid="comment-reply-text" variant="subtitle1">
            {localReply}
          </Reply>
        )}

        {smartReply && smartReplyId && showForm && (
          <LeifGPT
            commentId={id}
            patientId={Number(patientId)}
            smartReply={smartReply}
            smartReplyId={smartReplyId}
            onEdit={value => {
              form.setValue("body", value);
              form.setFocus("body");
            }}
            onSubmit={value => {
              handleSubmitComment(value);
            }}
            dismissEvent={AnalyticsEvents.LEIF_GPT.COMMENTS.SUGGESTION_DISMISSED}
            editEvent={AnalyticsEvents.LEIF_GPT.COMMENTS.SUGGESTION_EDITED}
            presentEvent={AnalyticsEvents.LEIF_GPT.COMMENTS.SUGGESTION_PRESENTED}
            submitEvent={AnalyticsEvents.LEIF_GPT.COMMENTS.SUGGESTION_SUBMITTED}
          />
        )}

        {showForm && (
          <>
            <MessageInput
              form={form}
              onSubmit={onSubmit}
              placeholder={t("patients.comments.reply_placeholder")}
              slots={{ calendar: false, emoji: true, quickReplies: true }}
            />
            {error !== null && (
              <Notification variant="danger" style={{ marginBottom: "5px" }}>
                {t("errors.generic")}
              </Notification>
            )}
          </>
        )}
      </WidgetBox>

      {previewActivity && <ActivityPreviewModal activity={previewActivity} onClose={() => setPreviewActivity(null)} />}
    </div>
  );
};

const LevelBadge = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.colors.redesign.db70};
`;

const CommentRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  ${props => props.theme.belowMobileBreakpoint} {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
`;

const Reply = styled(Typography)`
  box-sizing: border-box;
  width: calc(100% - 35px);
  white-space: pre-wrap;
`;
